import {
  Box,
  Button,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Code,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiX,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import PartySelectById from "./PartySelectById";
import { debounce } from "lodash";
import { errorFromResponse } from "./utils/apiErrors";

const UsersEditor = ({ user, onClose, isNew }) => {
  const [userHeader, setUserHeader] = useState({});
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (user) {
      setUserHeader({ ...user, roles: undefined });
      setRoles(user.roles || []);
    }
  }, [user]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setUserHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 25),
    []
  );

  const handleSave = async () => {
    try {
      const fullUser = { ...userHeader, roles };
      const response = await fetch(
        isNew
          ? "https://auth.poc.mrc.ai/api/users"
          : `https://auth.poc.mrc.ai/api/users/${userHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullUser),
        }
      );

      if (!response.ok) {
        // 403: ROLE_OUTSIDE_ORGANISATION, SHARED_USER, PLATFORM_ADMIN_ONLY
        // 409: EMAIL_IN_USE
        throw await errorFromResponse(
          response,
          `Failed to ${isNew ? "create" : "save"} user. Status: ${response.status}`
        );
      }

      const result = await response.json();

      // When an organisation admin creates a user whose email already exists,
      // the API answers 200 (not 201) with that existing user, now holding
      // this organisation's roles.
      const addedToExisting = isNew && response.status === 200;

      toast({
        title: addedToExisting
          ? "Existing user updated"
          : isNew
          ? "User created"
          : "User saved",
        description: addedToExisting
          ? `A user with this email already exists; your organisation's roles were added to them.`
          : isNew
          ? "New user was added."
          : "Changes saved successfully.",
        status: "success",
        duration: addedToExisting ? 6000 : 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/users/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving user:", error);

      toast({
        title:
          error.status === 403
            ? "Not allowed"
            : error.status === 409
            ? "Email already in use"
            : "Save failed",
        description:
          error.message || "An error occurred while saving the user.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit User</Heading>
                <Tooltip label="Save User" aria-label="Save User Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>User Details</Tab>
              <Tab>Roles</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={userHeader._id} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                    <FormControl>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        value={userHeader.firstName || ""}
                        onChange={(e) =>
                          handleHeaderChange("firstName", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        value={userHeader.lastName || ""}
                        onChange={(e) =>
                          handleHeaderChange("lastName", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input
                        value={userHeader.email || ""}
                        onChange={(e) =>
                          handleHeaderChange("email", e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 5 }} spacing={6} mt={4}>
                    <CodesetSelect
                      label="Active?"
                      codesetCode="JVRI018"
                      value={
                        userHeader.isActive
                          ? { code: userHeader.isActive, description: "" }
                          : null
                      }
                      onChange={(val) =>
                        handleHeaderChange("isActive", val?.code)
                      }
                    />

                    <CodesetSelect
                      label="Developer?"
                      codesetCode="JVRI018"
                      value={
                        userHeader.isDeveloper
                          ? { code: userHeader.isDeveloper, description: "" }
                          : null
                      }
                      onChange={(val) =>
                        handleHeaderChange("isDeveloper", val?.code)
                      }
                    />

                    <CodesetSelect
                      label="Admin?"
                      codesetCode="JVRI018"
                      value={
                        userHeader.isAdmin
                          ? { code: userHeader.isAdmin, description: "" }
                          : null
                      }
                      onChange={(val) =>
                        handleHeaderChange("isAdmin", val?.code)
                      }
                    />

                    <CodesetSelect
                      label="SuperAdmin?"
                      codesetCode="JVRI018"
                      value={
                        userHeader.isSuperAdmin
                          ? { code: userHeader.isSuperAdmin, description: "" }
                          : null
                      }
                      onChange={(val) =>
                        handleHeaderChange("isSuperAdmin", val?.code)
                      }
                    />
                  </SimpleGrid>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack alignItems="center" mb={4}>
                    <Heading size="md">Roles</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="md"
                      onClick={() =>
                        setRoles([...roles, { organisationId: "", role: "" }])
                      }
                      aria-label="Add Role"
                    />
                  </HStack>
                  <Box overflowX="auto">
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px" }}>
                            Organisation
                          </th>
                          <th style={{ textAlign: "left", padding: "8px" }}>
                            Role
                          </th>
                          <th style={{ width: "50px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles.map((role, index) => (
                          <tr key={index}>
                            <td style={{ padding: "8px" }}>
                              <PartySelectById
                                label=""
                                selectedPartyId={role.organisationId || ""}
                                onChange={(partyId) => {
                                  const newRoles = [...roles];
                                  newRoles[index].organisationId = partyId;
                                  setRoles(newRoles);
                                }}
                                size="sm"
                              />
                            </td>
                            <td style={{ padding: "8px" }}>
                              <CodesetSelect
                                codesetCode="MRCAI004"
                                value={
                                  role.role
                                    ? { code: role.role, description: "" }
                                    : null
                                }
                                onChange={(val) => {
                                  const newRoles = [...roles];
                                  newRoles[index].role = val.code;
                                  setRoles(newRoles);
                                }}
                                size="sm"
                              />
                            </td>
                            <td style={{ padding: "8px" }}>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="sm"
                                onClick={() => {
                                  const newRoles = roles.filter(
                                    (_, i) => i !== index
                                  );
                                  setRoles(newRoles);
                                }}
                                aria-label="Remove Role"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <VStack spacing={6} align="stretch"></VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default UsersEditor;
